import React from "react"
import _t from "@core/i18n"
import { PeriodInfo } from "@core/period-info"
import { get } from "lodash"
import { PriceVariantType } from "@onestore/hel/dist/components/molecules/PriceMol/priceMol.types"
import type { ProductCardMolProps } from "@onestore/hel/dist/components/molecules/ProductCardMol"
import DetailedPrice from "@gatsby-plugin-generic-page/components/DetailedPrice"
import getProductCardPeriodText from "@gatsby-plugin-generic-page/helpers/getProductCardPeriodText"
import type { ProductCardItem } from "@gatsby-plugin-marketplace/fragments/marketplaceProductCard"
import Price from "~/components/AdvancedProductCard/components/Price"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import useLinkPropertiesCollection from "~/hooks/useLinkPropertiesCollection"
import usePriceConfigurationQuery from "~/hooks/usePriceConfigurationQuery"
import assetsSquidexToImageObject from "~/lib/assetsSquidexToImageObject"
import isEmpty from "~/lib/isEmpty"

export default function useProductCards(
  products: ProductCardItem[]
): ProductCardMolProps[] {
  const { showDetailedPrices } = usePriceConfigurationQuery()

  const linkProps = useLinkPropertiesCollection(
    products
      .filter(
        (product) => product.flatData.pages && product.flatData.pages.length
      )
      .map((product) => product.flatData.pages[0].flatData.url)
  )
  const { currentPriceType } = usePriceTypeContext()

  return products
    .filter(
      (product) => product.flatData.pages && product.flatData.pages.length
    )
    .map((productItem) => {
      if (!productItem.flatData.pages.length) {
        return null
      }

      const defaultCloudBluePlan = get(
        productItem,
        "flatData.saleConfigurations[0].flatData.defaultCloudBluePlan"
      )

      const tooltip = !isEmpty(defaultCloudBluePlan)
        ? get(
            defaultCloudBluePlan[0],
            "flatData.promotionPriceTooltip[0].flatData.text"
          )
        : undefined

      const defaultCloudBluePlanPeriods = !isEmpty(defaultCloudBluePlan)
        ? defaultCloudBluePlan[0].flatData.periods
        : undefined

      const defaultPeriod = !isEmpty(defaultCloudBluePlanPeriods)
        ? defaultCloudBluePlanPeriods.find((period) => period.default)
        : undefined

      const periodInfo = defaultPeriod
        ? new PeriodInfo(defaultPeriod)
        : undefined

      const periodText = getProductCardPeriodText(periodInfo, currentPriceType)

      if (!periodInfo || !productItem.flatData.pages) {
        return null
      }

      const optionsMapping = get(
        productItem,
        "flatData.saleConfigurations[0].flatData.optionsMapping"
      )

      const prefix =
        !isEmpty(optionsMapping) || productItem.flatData.hasPricePrefix

      const priceInfo = !isEmpty(defaultCloudBluePlan)
        ? defaultCloudBluePlan[0].flatData.priceInfo
        : undefined

      const priceJSX = showDetailedPrices ? (
        <DetailedPrice
          variant={{ type: PriceVariantType.SMALL, align: "left" }}
          periodInfo={periodInfo}
          priceInfo={priceInfo}
        />
      ) : (
        <Price
          prefix={prefix ? _t("prices.from") : undefined}
          tooltip={tooltip}
          periodInfo={periodInfo}
          priceType={currentPriceType}
          periodText={periodText}
        />
      )

      const price = !isEmpty(periodInfo) ? priceJSX : null

      const customPriceText = get(
        productItem,
        "flatData.saleConfigurations[0].flatData.customPriceBox.priceText"
      )

      return {
        backgroundImage: assetsSquidexToImageObject(
          productItem.flatData.headerCardBg,
          productItem.flatData.cardDescription || ""
        ),
        logo: {
          image: assetsSquidexToImageObject(
            productItem.flatData.headerLogo,
            productItem.flatData.cardDescription || ""
          ),
        },
        title: productItem.flatData.name,
        titleHtmlTag: "h3",
        link: {
          ...linkProps[productItem.flatData.pages[0].flatData.url],
          title: productItem.flatData.name,
        },
        description: productItem.flatData.cardDescription,
        price: customPriceText || price,
        priceText: productItem.flatData.priceText,
      } as ProductCardMolProps
    })
    .filter((card) => card) as ProductCardMolProps[]
}
